import { memo, useEffect, useMemo, type CSSProperties, type Ref } from 'react';
import {
  docMotionSchema,
  layerWindow,
  soundSegments,
  sceneTimeline,
  stackDirection,
  type GroupLayer,
  type Layer,
  type TemplateDoc,
} from '@bannermatic/core';
import { ImageLayerView } from './layers/ImageLayerView';
import { QrLayerView } from './layers/QrLayerView';
import { ShapeLayerView } from './layers/ShapeLayerView';
import { TextLayerView } from './layers/TextLayerView';
import { requestRefit } from './autofit';
import { fillStyle } from './style';

export interface SceneProps {
  doc: TemplateDoc;
  /** `editor` shows placeholders for empty images and makes locked layers click-through. */
  mode?: 'render' | 'editor';
  watermark?: boolean;
  editingLayerId?: string | null;
  onTextCommit?: (layerId: string, text: string) => void;
  onTextEditInput?: (layerId: string) => void;
  resolveSrc?: (src: string) => string;
  className?: string;
  style?: CSSProperties;
  sceneRef?: Ref<HTMLDivElement>;
  /** Show a checkerboard where the background is transparent (editor only). */
  showTransparency?: boolean;
  /** Play video clips (animation preview, video render) instead of showing their poster. */
  videos?: boolean;
  /** Editor preview: this layer's clip plays on its own, with its sound settings. */
  liveVideo?: string | null;
  /** With `videos`: add the music track (the animation preview plays it along). */
  music?: boolean;
}

interface LayerViewProps {
  layer: Layer;
  /** Children of stacks, keyed by stack id. */
  childMap: Map<string, Layer[]>;
  canvas: { width: number; height: number };
  /** Set when the layer is laid out by a parent stack instead of absolutely positioned. */
  inStack?: {
    direction: 'vertical' | 'horizontal';
    alignItems: GroupLayer['alignItems'];
    /**
     * An auto stack laid out as a column with a growing picture: the picture takes the free
     * height and text blocks hug their content instead of splitting the space with it.
     */
    mediaFirst?: boolean;
  };
  editingLayerId?: string | null;
  mode: 'render' | 'editor';
  editing: boolean;
  onTextCommit?: (layerId: string, text: string) => void;
  onTextEditInput?: (layerId: string) => void;
  resolveSrc?: (src: string) => string;
  videoSeconds?: number;
  /** Videos: when each clip plays (its scene), ms. */
  videoWindows?: Map<string, [number, number]>;
  liveVideo?: string | null;
}

const FLEX_ALIGN = { start: 'flex-start', center: 'center', end: 'flex-end', stretch: 'stretch' };
const FLEX_JUSTIFY = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  'space-between': 'space-between',
};

/**
 * Size of a layer laid out by a stack (CSS flexbox does the actual layout). Along a column, text
 * and nested stacks take the height their content needs, so longer text pushes the next items;
 * `grow` takes the free space; everything else keeps its own size and may shrink to fit.
 */
function stackItemStyle(
  layer: Layer,
  stack: NonNullable<LayerViewProps['inStack']>,
): CSSProperties {
  const vertical = stack.direction === 'vertical';
  // A stack without auto layout keeps its own size, like a picture
  const flows = layer.type === 'text' || (layer.type === 'group' && layer.direction !== 'free');
  const rigid =
    layer.type === 'qr' ||
    (layer.type === 'shape' && (layer.shape === 'ellipse' || layer.shape === 'icon'));
  const stretch = stack.alignItems === 'stretch' && !rigid;
  const grow = layer.grow && !(flows && stack.mediaFirst);
  const hugMain = flows && vertical && !grow;
  const main = grow ? undefined : hugMain ? 'auto' : vertical ? layer.height : layer.width;
  const cross = stretch
    ? undefined
    : flows && !vertical
      ? 'auto'
      : vertical
        ? layer.width
        : layer.height;
  return {
    position: 'relative',
    flex: grow ? '1 1 0' : hugMain ? '0 0 auto' : '0 1 auto',
    // Text never gets narrower than its longest word (no mid-word breaks); others may shrink
    minWidth: layer.type === 'text' ? undefined : 0,
    minHeight: 0,
    [vertical ? 'height' : 'width']: main,
    [vertical ? 'width' : 'height']: cross,
    [vertical ? 'maxWidth' : 'maxHeight']: '100%',
    alignSelf: stretch ? 'stretch' : undefined,
  };
}

const LayerView = memo(function LayerView({
  layer,
  childMap,
  canvas,
  inStack,
  editingLayerId,
  mode,
  editing,
  onTextCommit,
  onTextEditInput,
  resolveSrc,
  videoSeconds,
  videoWindows,
  liveVideo,
}: LayerViewProps) {
  if (!layer.visible && mode === 'render') return null;
  const style: CSSProperties = {
    ...(inStack
      ? stackItemStyle(layer, inStack)
      : {
          position: 'absolute',
          left: layer.x,
          top: layer.y,
          width: layer.width,
          height: layer.height,
        }),
    transform: layer.rotation ? `rotate(${layer.rotation}deg)` : undefined,
    opacity: layer.opacity,
    display: layer.visible ? undefined : 'none',
    boxSizing: 'border-box',
    pointerEvents: mode === 'editor' && layer.locked && !editing ? 'none' : undefined,
  };

  let content;
  switch (layer.type) {
    case 'text':
      content = (
        <TextLayerView
          layer={layer}
          editing={editing}
          onCommit={(text) => onTextCommit?.(layer.id, text)}
          onEditInput={() => onTextEditInput?.(layer.id)}
        />
      );
      break;
    case 'image':
      content = (
        <ImageLayerView
          layer={layer}
          mode={mode}
          resolveSrc={resolveSrc}
          videoSeconds={videoSeconds}
          videoWindow={videoWindows?.get(layer.id)}
          live={liveVideo === layer.id}
        />
      );
      break;
    case 'shape':
      content = <ShapeLayerView layer={layer} />;
      break;
    case 'qr':
      content = <QrLayerView layer={layer} resolveSrc={resolveSrc} />;
      break;
    case 'group': {
      const direction = stackDirection(layer, canvas);
      if (direction === 'free') {
        // No auto layout: each layer where it was placed, from the stack's corner
        Object.assign(style, {
          borderRadius: layer.borderRadius || undefined,
          overflow: layer.clip ? 'hidden' : 'visible',
          ...fillStyle(layer.fill),
        } satisfies CSSProperties);
        content = (childMap.get(layer.id) ?? []).map((child) => (
          <LayerView
            key={child.id}
            layer={child}
            childMap={childMap}
            canvas={canvas}
            editingLayerId={editingLayerId}
            mode={mode}
            editing={editingLayerId === child.id}
            onTextCommit={onTextCommit}
            onTextEditInput={onTextEditInput}
            resolveSrc={resolveSrc}
            videoSeconds={videoSeconds}
            videoWindows={videoWindows}
            liveVideo={liveVideo}
          />
        ));
        break;
      }
      Object.assign(style, {
        display: layer.visible ? 'flex' : 'none',
        flexDirection: direction === 'vertical' ? 'column' : 'row',
        gap: layer.gap,
        paddingTop: layer.padding.top,
        paddingRight: layer.padding.right,
        paddingBottom: layer.padding.bottom,
        paddingLeft: layer.padding.left,
        alignItems: FLEX_ALIGN[layer.alignItems],
        justifyContent: FLEX_JUSTIFY[layer.justify],
        borderRadius: layer.borderRadius || undefined,
        overflow: layer.clip ? 'hidden' : 'visible',
        ...fillStyle(layer.fill),
      } satisfies CSSProperties);
      const children = childMap.get(layer.id) ?? [];
      const inStack = {
        direction,
        alignItems: layer.alignItems,
        mediaFirst:
          layer.direction === 'auto' &&
          direction === 'vertical' &&
          children.some((c) => c.grow && c.visible && (c.type === 'image' || c.type === 'shape')),
      };
      content = children.map((child) => (
        <LayerView
          key={child.id}
          layer={child}
          childMap={childMap}
          canvas={canvas}
          inStack={inStack}
          editingLayerId={editingLayerId}
          mode={mode}
          editing={editingLayerId === child.id}
          onTextCommit={onTextCommit}
          onTextEditInput={onTextEditInput}
          resolveSrc={resolveSrc}
          videoSeconds={videoSeconds}
          videoWindows={videoWindows}
          liveVideo={liveVideo}
        />
      ));
      break;
    }
  }

  return (
    <div
      className="bm-layer"
      data-layer-id={layer.id}
      data-layer-name={layer.name}
      data-layer-type={layer.type}
      // Editor only: a text drawn with a stand-in font (highlighted by the editor's CSS)
      data-missing-font={
        mode === 'editor' && layer.type === 'text' && layer.missingFont
          ? layer.missingFont
          : undefined
      }
      data-locked={layer.locked ? 'true' : undefined}
      style={style}
    >
      {content}
    </div>
  );
});

/**
 * Free-plan watermark: a translucent "Bannermatic" pattern tiled diagonally over the whole
 * canvas. White text with a dark outline stays visible on both light and dark designs.
 */
function Watermark({ width, height }: { width: number; height: number }) {
  const size = Math.max(12, Math.round(Math.min(width, height) * 0.045));
  // The label is ~6.5em wide; the tile leaves room so a centred label is never clipped
  const tileW = size * 10;
  const tileH = size * 5;
  const text = (x: number, y: number) => (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
      fontFamily="sans-serif"
      fontWeight={700}
      fontSize={size}
      fill="rgba(255,255,255,0.32)"
      stroke="rgba(0,0,0,0.16)"
      strokeWidth={Math.max(0.5, size * 0.035)}
    >
      Bannermatic
    </text>
  );
  return (
    <svg
      data-watermark
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
    >
      <defs>
        <pattern
          id="bm-watermark"
          width={tileW}
          height={tileH}
          patternUnits="userSpaceOnUse"
          patternTransform={`rotate(-30 ${width / 2} ${height / 2})`}
        >
          {/* Brick-like stagger: the second row is shifted by half a tile; its label is split
              across the tile edges and drawn on both sides so neighbouring tiles join it up */}
          {text(tileW / 2, tileH / 4)}
          {text(0, (tileH * 3) / 4)}
          {text(tileW, (tileH * 3) / 4)}
        </pattern>
      </defs>
      <rect width={width} height={height} fill="url(#bm-watermark)" />
    </svg>
  );
}

const CHECKERBOARD = 'repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 50% / 20px 20px';

export function Scene({
  doc,
  mode = 'render',
  watermark,
  editingLayerId,
  onTextCommit,
  onTextEditInput,
  resolveSrc,
  className,
  style,
  sceneRef,
  showTransparency,
  videos,
  liveVideo,
  music: withMusic = true,
}: SceneProps) {
  // Music, voice-over and effects of the animation preview (clips play their own sound)
  const sounds = useMemo(
    () =>
      videos && withMusic
        ? soundSegments({ ...doc, layers: [] }).filter((x) => x.kind !== 'clip')
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [videos, withMusic, doc.motion],
  );
  const videoWindows = useMemo(
    () =>
      videos
        ? new Map(
            doc.layers
              .filter((l) => l.type === 'image' && l.video)
              .map((l) => [l.id, layerWindow(doc, l)] as [string, [number, number]]),
          )
        : undefined,
    [videos, doc],
  );
  const videoSeconds = useMemo(
    () =>
      videos ? sceneTimeline(docMotionSchema.parse(doc.motion ?? {})).duration / 1000 : undefined,
    [videos, doc.motion],
  );
  // Re-run auto-fit whenever a web font finishes loading.
  useEffect(() => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (!fonts) return;
    const onDone = () => requestRefit();
    fonts.addEventListener('loadingdone', onDone);
    void fonts.ready.then(onDone);
    return () => fonts.removeEventListener('loadingdone', onDone);
  }, []);

  // Stack children render inside their stack; anything with a dangling parent stays top-level
  const { topLevel, childMap } = useMemo(() => {
    const groups = new Set(doc.layers.filter((l) => l.type === 'group').map((l) => l.id));
    const map = new Map<string, Layer[]>();
    const top: Layer[] = [];
    for (const l of doc.layers) {
      if (l.parentId && l.parentId !== l.id && groups.has(l.parentId)) {
        const list = map.get(l.parentId) ?? [];
        list.push(l);
        map.set(l.parentId, list);
      } else top.push(l);
    }
    return { topLevel: top, childMap: map };
  }, [doc.layers]);

  const canvas = useMemo(() => ({ width: doc.width, height: doc.height }), [doc.width, doc.height]);

  const transparentBg =
    !doc.background || (doc.background.type === 'solid' && doc.background.color === 'transparent');

  return (
    <div
      ref={sceneRef}
      className={className ? `bm-scene ${className}` : 'bm-scene'}
      data-scene
      style={{
        position: 'relative',
        width: doc.width,
        height: doc.height,
        overflow: 'hidden',
        ...(transparentBg && showTransparency ? { background: CHECKERBOARD } : {}),
        ...style,
      }}
    >
      {!transparentBg && (
        <div
          data-background
          style={{ position: 'absolute', inset: 0, ...fillStyle(doc.background) }}
        />
      )}
      {topLevel.map((layer) => (
        <LayerView
          key={layer.id}
          layer={layer}
          childMap={childMap}
          canvas={canvas}
          editingLayerId={editingLayerId}
          mode={mode}
          editing={editingLayerId === layer.id}
          onTextCommit={onTextCommit}
          onTextEditInput={onTextEditInput}
          resolveSrc={resolveSrc}
          videoSeconds={videoSeconds}
          videoWindows={videoWindows}
          liveVideo={liveVideo}
        />
      ))}
      {videoSeconds !== undefined &&
        sounds.map((x) => (
          // The player keeps each sound in sync with the playhead
          <audio
            key={x.id}
            data-bm-sound={x.id}
            src={resolveSrc ? resolveSrc(x.src) : x.src}
            preload="auto"
          />
        ))}
      {watermark && <Watermark width={doc.width} height={doc.height} />}
    </div>
  );
}
